import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  useDeleteApiMarksByIdMutation,
  useGetApiBookmarkFoldersQuery,
  useGetApiNotesByVersionIdQuery,
  useGetApiVersionsInvalidMarksQuery,
} from '../../../redux/store/api/api';
import {
  selectActiveNote,
  selectActiveVersion,
  setActiveNote,
} from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';

interface IDeleteMarkFromNoteDialogProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

function DeleteMarkFromNoteDialog({
  show,
  setShow,
}: IDeleteMarkFromNoteDialogProps): JSX.Element {
  const { t: translation } = useTranslation();
  const markId = useAppSelector(selectActiveNote).mark?.id;
  const version = useAppSelector(selectActiveVersion);
  const [
    deleteMark,
    { isError: deleteMarkIsError, isLoading, error: deleteMarkError },
  ] = useDeleteApiMarksByIdMutation();
  const { refetch: refetchNotes } = useGetApiNotesByVersionIdQuery(
    version.id
      ? {
          versionId: version.id,
        }
      : skipToken,
  );
  const { refetch: refetchBookmarkTree } = useGetApiBookmarkFoldersQuery();
  const { refetch: refetchInvalidMarks } = useGetApiVersionsInvalidMarksQuery();
  const dispatch = useAppDispatch();

  const handleDeleteMark = (): void => {
    deleteMark({
      id: markId || '',
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'DeleteMarkSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        dispatch(setActiveNote({}));
        setShow(false);
        refetchBookmarkTree();
        refetchInvalidMarks();
        refetchNotes();
      });
  };

  useEffect(() => {
    if (deleteMarkIsError) {
      dispatch(
        addMessage({
          id: 'DeleteMarkError',
          variant: 'danger',
          messageKeyBody:
            deleteMarkError && 'data' in deleteMarkError
              ? deleteMarkError.data?.messageKey
              : 'unknownError',
        }),
      );
    }
  }, [deleteMarkIsError]);

  return (
    <CustomDialog
      titleId='DeleteMarkFromNoteDialog'
      dialogTitle={translation('deleteMarkOfNote')}
      show={show}
      closeFunction={() => {
        setShow(false);
        dispatch(setActiveNote({}));
      }}
      actionFunction={handleDeleteMark}
      closeTitle={translation('cancel')}
      actionTitle={translation('delete')}
      actionButtonVariant='outline-danger'
      actionButtonDisabled={isLoading}>
      {isLoading && <Loader />}
      {!isLoading && <p>{translation('deleteMarkPermanently')}</p>}
    </CustomDialog>
  );
}

export default DeleteMarkFromNoteDialog;
